import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import apiClient from '../services/api';

// Página real de suscripción — reemplaza el stub viejo (planes ficticios
// a $0, botón "Upgrade" que solo cambiaba un campo en la base sin cobrar
// nada). Acá los links de pago SÍ existen: cada plan tiene un botón que
// crea un cobro real en Mercado Pago o PayPal (ver endpoints/payments.py
// en el backend) y redirige al cliente a pagar. Mientras el cliente no
// pague, is_paid queda en false — el acceso al aplicativo en sí no está
// bloqueado todavía (eso es una decisión de producto aparte: si Augusto
// quiere que además se le corte el acceso a quien no pagó, hay que
// agregar ese chequeo en las rutas privadas del frontend).
// 4 ago 2026: las páginas por mes de cada plan vienen de PLAN_PAGE_QUOTA
// en backend_new/app/core/plans.py -- si se cambian ahí, hay que
// actualizarlas acá también (no hay un solo lugar compartido entre
// frontend y backend para esto).
//
// 3 sep 2026 (Augusto: "necesito un botón de suscripción separado para
// testers" -> "quiero que esto nuevo... lo tenga en mi cuenta admin en
// la parte ADMIN"): la tarjeta de "Regalar acceso" que había acá se
// movió a ClientesPage.js (ruta /clientes, sección ADMIN del sidebar) --
// esta página es de cara al cliente (cualquiera que pague la ve), así
// que no es el lugar correcto para una herramienta de admin. Ver
// ClientesPage.js para la implementación.
const PLAN_FEATURES = {
  // 4 ago 2026: diferenciadores gratis (sin IA) agregados al plan
  // Personal -- ver bulk_import.py y admin.py (debt-reminders/send).
  personal: ['Cuentas y deudas ilimitadas', 'Transacciones y presupuestos', 'Importador de estados de cuenta con IA', '60 páginas de estados de cuenta al mes (o compra extra)', 'Importador de CSV/Excel sin costo adicional', 'Recordatorios de vencimiento de deudas por correo (opcional)'],
  pro: ['Todo lo de Personal', 'Categorías y reportes avanzados', 'Pregúntale a la IA: preguntas libres sobre tus finanzas, con gráficos', 'Constructor de presupuesto con IA: te sugiere cuánto gastar por categoría y qué recortar', '150 páginas de estados de cuenta al mes (o compra extra)', 'Soporte prioritario'],
  // 17 sep 2026: los planes negocio_basico / negocio_pro ya no se venden
  // en PCM (las empresas van a CapitalOptima); el backend dejó de
  // listarlos en /subscription/plans.
};

const Subscription = () => {
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // "personal-mercadopago" | "pro-paypal" | ...
  const [notice, setNotice] = useState(null);

  const loadData = () => {
    setLoading(true);
    Promise.all([apiClient.get('/subscription/plans'), apiClient.get('/subscription/current')])
      .then(([plansRes, currentRes]) => {
        setPlans(plansRes.data);
        setCurrent(currentRes.data);
      })
      .catch(() => setNotice({ tipo: 'error', texto: 'No se pudo cargar la información de suscripción.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si Mercado Pago o PayPal redirigen de vuelta acá, resolvemos el
  // resultado. PayPal manda ?token={order_id} en la URL de retorno — hay
  // que capturar el pago explícitamente. Mercado Pago ya marca el pago
  // vía webhook del lado del servidor, así que acá solo se refresca el
  // estado (puede tardar unos segundos en llegar el webhook).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pago = params.get('pago');
    const proveedor = params.get('proveedor');
    const token = params.get('token');

    if (pago === 'exitoso' && proveedor === 'paypal' && token) {
      apiClient
        .post(`/payments/paypal/capture/${token}`)
        .then((res) => {
          if (res.data.success) {
            setNotice({ tipo: 'success', texto: '¡Pago con PayPal confirmado!' });
            loadData();
          } else {
            setNotice({ tipo: 'warning', texto: 'PayPal no confirmó el pago todavía. Si ya pagaste, escríbele a soporte.' });
          }
        })
        .catch(() => setNotice({ tipo: 'error', texto: 'No se pudo confirmar el pago con PayPal.' }))
        .finally(() => window.history.replaceState({}, '', '/subscription'));
    } else if (pago === 'exitoso') {
      setNotice({ tipo: 'success', texto: 'Pago recibido — confirmando con Mercado Pago, puede tardar unos segundos. Si no se actualiza, recarga esta página.' });
      window.history.replaceState({}, '', '/subscription');
      setTimeout(loadData, 4000);
    } else if (pago === 'fallido' || pago === 'cancelado') {
      setNotice({ tipo: 'warning', texto: 'El pago no se completó.' });
      window.history.replaceState({}, '', '/subscription');
    } else if (pago === 'pendiente') {
      setNotice({ tipo: 'info', texto: 'Tu pago está pendiente de confirmación.' });
      window.history.replaceState({}, '', '/subscription');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iniciarPago = async (planId, proveedor) => {
    setPaying(`${planId}-${proveedor}`);
    setNotice(null);
    try {
      const res = await apiClient.post(`/payments/${proveedor}/checkout?plan=${planId}`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setNotice({ tipo: 'error', texto: 'No se recibió el link de pago.' });
        setPaying(null);
      }
    } catch (err) {
      setNotice({
        tipo: 'error',
        texto: err.response?.data?.detail || `No se pudo iniciar el pago con ${proveedor}.`,
      });
      setPaying(null);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Suscripción
      </Typography>

      {notice && (
        <Alert severity={notice.tipo} sx={{ mb: 3 }} onClose={() => setNotice(null)}>
          {notice.texto}
        </Alert>
      )}

      {current && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Plan actual: <strong>{current.plan}</strong>{' '}
            <Chip
              size="small"
              label={current.pago ? `Pagó (${current.proveedor_pago})` : 'Sin pago registrado'}
              color={current.pago ? 'success' : 'default'}
              sx={{ ml: 1 }}
            />
          </Typography>
        </Box>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} key={plan.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ textTransform: 'capitalize' }}>
                    {plan.nombre}
                  </Typography>
                  {plan.consultar_precio ? (
                    <Typography variant="h4" color="primary" gutterBottom>
                      Consultar precio
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="h4" color="primary" gutterBottom>
                        S/ {plan.precio_pen.toFixed(2)} <Typography component="span" variant="body2" color="text.secondary">/ mes</Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        (≈ ${plan.precio_usd.toFixed(2)} USD si pagas con PayPal)
                      </Typography>
                    </>
                  )}
                  <ul>
                    {(PLAN_FEATURES[plan.id] || []).map((f) => (
                      <li key={f}>
                        <Typography variant="body2" color="text.secondary">{f}</Typography>
                      </li>
                    ))}
                  </ul>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {plan.consultar_precio ? (
                      <Button
                        variant="contained"
                        fullWidth
                        href="mailto:augusto@darwinoptima.com?subject=Plan%20Negocio%20Pro%20-%20PCM"
                      >
                        Conversemos
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={paying === `${plan.id}-mercadopago`}
                          onClick={() => iniciarPago(plan.id, 'mercadopago')}
                        >
                          {paying === `${plan.id}-mercadopago` ? 'Redirigiendo…' : 'Pagar con Mercado Pago (Visa/Mastercard)'}
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          disabled={paying === `${plan.id}-paypal`}
                          onClick={() => iniciarPago(plan.id, 'paypal')}
                        >
                          {paying === `${plan.id}-paypal` ? 'Redirigiendo…' : 'Pagar con PayPal'}
                        </Button>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Subscription;
